/**
 * Combine Lots — shared selection store (super-combo main map).
 *
 * Maintains an in-memory list of BBLs the user has shift-clicked to "combine"
 * on the main super-combo map. Phase 1: shift-click toggles membership and
 * the map renders a dot-pattern fill on members. Phase 2 will surface the
 * list as a Tabulator grid in the results pane.
 *
 * Differences vs. the block-analyzer selection store (which inspired this):
 *   - Singleton attached to `window.PsCombinedLots` — the main map lives for
 *     the page lifetime, so there's no per-container scoping needed.
 *   - No `allowed` set — any lot anywhere on the map can be toggled.
 *   - Caches the clicked feature's GeoJSON geometry alongside each BBL, so
 *     the deck.gl pattern overlay has polygons to render without needing a
 *     separate geometry fetch. This is the "Option A" geometry strategy
 *     documented in GH issue #270; tile-clipped polygons at low zoom are an
 *     accepted phase-1 limitation.
 *
 * Persistence: in-memory only — list is wiped on page reload (intentional
 * for phase 1; phase 2 may URL-hash-sync).
 *
 * Exposed as `window.PsCombinedLots`.
 */

const listeners = new Set();
// bbl (string) -> {geometry, properties} captured at click time.
// Geometry is a GeoJSON geometry object (Polygon/MultiPolygon) from
// Mapbox's queryRenderedFeatures result; properties is a shallow copy of
// the feature's `properties` (mostly for debugging / future use).
const entries = new Map();

function notify() {
  const snapshot = Array.from(entries.keys());
  listeners.forEach((fn) => {
    try { fn(snapshot); } catch (err) { console.error('[combined-lots] listener error', err); }
  });
}

function asKey(bbl) { return String(bbl); }

const store = {
  /* ----- accessors ----- */
  /** Array of BBLs (strings) currently in the combined list. */
  getSelected() { return Array.from(entries.keys()); },
  isSelected(bbl) { return entries.has(asKey(bbl)); },
  size() { return entries.size; },

  /**
   * Click-time properties cached for a BBL (the Mapbox feature's
   * `properties` object as snapshotted by `add`/`toggle`). Used by the
   * data loader to build placeholder grid rows for BBLs whose server
   * fetch hasn't landed yet (so the row appears in the table
   * immediately, with address/lot/etc. populated from tile data and
   * server-only fields like owner/SF shown as "Loading…").
   * Returns null if the BBL isn't in the store or wasn't given
   * properties at click time.
   */
  getProperties(bbl) {
    const entry = entries.get(asKey(bbl));
    return entry ? entry.properties || null : null;
  },

  /** GeoJSON FeatureCollection of all combined lots (for the overlay). */
  getFeatureCollection() {
    const features = [];
    entries.forEach((entry, bbl) => {
      if (!entry || !entry.geometry) { return; }
      features.push({
        type: 'Feature',
        geometry: entry.geometry,
        properties: Object.assign({}, entry.properties || {}, { bbl: bbl }),
      });
    });
    return { type: 'FeatureCollection', features: features };
  },

  /* ----- mutations ----- */
  /**
   * Add a BBL with its captured geometry. No-op if already present.
   * Returns true if the store changed.
   */
  add(bbl, geometry, properties) {
    const key = asKey(bbl);
    if (entries.has(key)) { return false; }
    entries.set(key, { geometry: geometry || null, properties: properties || null });
    notify();
    return true;
  },

  /** Remove a BBL. Returns true if the store changed. */
  remove(bbl) {
    const key = asKey(bbl);
    if (!entries.delete(key)) { return false; }
    notify();
    return true;
  },

  /**
   * Toggle membership. When adding, `geometry` and `properties` are
   * required (we have no other way to render the dot pattern). When
   * removing, they're ignored.
   * Returns the new membership state (true = now selected).
   */
  toggle(bbl, geometry, properties) {
    const key = asKey(bbl);
    if (entries.has(key)) {
      entries.delete(key);
      notify();
      return false;
    }
    entries.set(key, { geometry: geometry || null, properties: properties || null });
    notify();
    return true;
  },

  /** Clear all entries. Returns true if the store changed. */
  clear() {
    if (entries.size === 0) { return false; }
    entries.clear();
    notify();
    return true;
  },

  /**
   * Replace the cached geometry for an already-selected BBL. Used by
   * the data-loader (GH #270 Option D) to swap the click-time
   * tile-clipped geometry for the canonical full-lot polygon fetched
   * from the server. No-op if the BBL isn't in the store (e.g. user
   * removed it between the fetch starting and resolving).
   *
   * Returns true if the store changed (notify fires).
   */
  updateGeometry(bbl, geometry) {
    const key = asKey(bbl);
    const entry = entries.get(key);
    if (!entry) { return false; }
    if (!geometry) { return false; }
    if (entry.geometry === geometry) { return false; }
    entries.set(key, Object.assign({}, entry, { geometry: geometry }));
    notify();
    return true;
  },

  /* ----- subscribe ----- */
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

if (typeof window !== 'undefined') {
  window.PsCombinedLots = store;
}

export default store;
